/**
 * What someone sees before they sign in.
 *
 * The home page used to be the sign-in form with a one-line description above
 * it, which told a visitor the product's name and nothing about whether it was
 * for them. This says what it is, what it is not, and what using it involves.
 *
 * Every claim here comes from `docs/requirements/01-product-brief.md` rather
 * than from marketing invention. That is deliberate: a landing page that
 * promises something the requirements do not describe is a page that will be
 * wrong by the next release, and the brief is the thing being kept current.
 *
 * It is one column of prose with a heading structure, not a layout. The app is
 * read on a phone (NFR-UX-1), everything here has to clear the contrast and
 * reflow criteria the rest of the screens do, and a marketing page that needed
 * its own grid system would be a second design to maintain for the screen seen
 * least often by the person who uses this.
 */
export function Landing({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div className="landing">
      <section aria-labelledby="landing-heading">
        <h1 id="landing-heading">Intentionalist</h1>
        <p className="lede">A manifest of what you own, and what you have decided about it.</p>

        <p>
          Belongings accumulate by default rather than by decision — through gifts, impulses,
          replacements that never displaced the thing they replaced, and inertia. The cost is not
          only clutter. It is not being able to answer simple questions about your own things.
        </p>

        {/*
          The button comes before the explanation, not after it.
          Someone who already knows what this is should not have to read the
          page to get past it, and someone who does not can keep scrolling.
        */}
        <div className="actions">
          <button type="button" className="primary" onClick={onSignIn}>
            Sign in
          </button>
        </div>
      </section>

      <section aria-labelledby="landing-questions">
        <h2 id="landing-questions">Questions a pile of possessions cannot answer</h2>
        <ul className="landing-list">
          <li>What do I actually own, and where is it?</li>
          <li>What have I not touched in a year?</li>
          <li>What do I genuinely love, and what am I merely storing?</li>
          <li>What is worn out and worth replacing with something better?</li>
        </ul>
      </section>

      <section aria-labelledby="landing-verdicts">
        <h2 id="landing-verdicts">Every item gets a verdict</h2>
        <p>
          The catalog is the product. Each thing in it carries not just its facts — what it is, what
          it cost, where it lives — but your judgment about it.
        </p>

        <dl className="landing-verdicts">
          <dt>Keep</dt>
          <dd>I love this and it earns its place.</dd>

          <dt>Uncertain</dt>
          <dd>I have not decided. Ask me again later.</dd>

          <dt>Release</dt>
          <dd>This no longer serves me — donate, sell, or discard.</dd>

          <dt>Invest</dt>
          <dd>The need is real but this object is not good enough. Replace it deliberately.</dd>
        </dl>

        <p>
          That last one is the difference between this and a decluttering app. <em>Invest</em> turns
          a worn-out thing into a deliberate future purchase instead of an impulsive one.
        </p>
      </section>

      <section aria-labelledby="landing-loop">
        <h2 id="landing-loop">A loop, not a purge</h2>
        <p>
          Catalog a thing once, live with it, and revisit the decision when it is worth revisiting.
          Verdicts are never overwritten, so <em>I keep deciding to keep this</em> becomes visible
          over time — and so does the opposite.
        </p>
        <p className="muted">Capture → live with it → review → decide → act, and round again.</p>
      </section>

      <section aria-labelledby="landing-not">
        <h2 id="landing-not">What it is not</h2>
        <p>
          Not a home inventory for insurance: those optimize for proof of loss, not for reflection.
          Not a one-time purge: those produce an event, not a durable record. This is an ongoing,
          revisable manifest paired with your own judgment.
        </p>
      </section>

      <section aria-labelledby="landing-practical">
        <h2 id="landing-practical">Practically</h2>
        <ul className="landing-list">
          <li>Works offline. Capture in a cellar with no signal; it syncs when there is some.</li>
          <li>Photo-first, because a photograph identifies a thing faster than a description.</li>
          <li>Installs to a phone's home screen, and runs in a browser on anything else.</li>
          <li>Your catalog is exportable, in full, whenever you want it.</li>
        </ul>

        <div className="actions">
          <button type="button" className="primary" onClick={onSignIn}>
            Sign in
          </button>
        </div>
      </section>
    </div>
  );
}
